import { motion } from "framer-motion";
import { TrendingUp, Activity, MapPin, Smile } from "lucide-react";
import { useTrendTopics, type Timeframe } from "@/hooks/useTrendTopics";

const PLATFORM_LABEL: Record<string, string> = {
  reddit: "Reddit",
  gdelt: "News",
  google_trends: "Google",
  youtube: "YouTube",
};

export default function TrendStatsRow({ timeframe }: { timeframe: Timeframe }) {
  const { data: topics = [], isLoading } = useTrendTopics(timeframe);

  const top = topics[0];
  const platforms: Record<string, number> = {};
  let pos = 0, neu = 0, neg = 0, n = 0;
  for (const t of topics) {
    if (t.platform_breakdown) {
      for (const [k, v] of Object.entries(t.platform_breakdown)) {
        platforms[k] = (platforms[k] ?? 0) + Number(v);
      }
    }
    pos += t.sentiment_pos; neu += t.sentiment_neu; neg += t.sentiment_neg; n++;
  }
  const totalPlatform = Math.max(1, Object.values(platforms).reduce((a, b) => a + b, 0));
  const platformBars = Object.entries(platforms)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4);
  const sentN = Math.max(1, n);
  const sPos = Math.round((pos / sentN) * 100);
  const sNeu = Math.round((neu / sentN) * 100);
  const sNeg = Math.max(0, 100 - sPos - sNeu);

  const tiles = [
    {
      icon: TrendingUp,
      label: "Top trending topic",
      value: top?.focus_area || top?.topic?.slice(0, 28) || "—",
      change: top ? `${top.momentum_pct >= 0 ? "+" : ""}${top.momentum_pct}%` : "",
      empty: !top,
    },
    {
      icon: Activity,
      label: "Volume by platform",
      custom: (
        <div className="space-y-1.5 mt-1">
          {platformBars.length === 0 && <span className="text-xs text-muted-foreground">No data yet</span>}
          {platformBars.map(([k, v]) => (
            <div key={k} className="flex items-center gap-2 text-xs">
              <span className="w-14 text-muted-foreground">{PLATFORM_LABEL[k] ?? k}</span>
              <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                <div className="h-full bg-primary" style={{ width: `${(v / totalPlatform) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      ),
    },
    {
      icon: MapPin,
      label: "Geographic signal",
      value: top?.geo_top_county || "Statewide",
      change: top?.geo_top_county_share ? `${Math.round(top.geo_top_county_share * 100)}% share` : "",
    },
    {
      icon: Smile,
      label: "Sentiment",
      custom: (
        <div className="mt-2">
          <div className="flex h-2 rounded-full overflow-hidden bg-muted">
            <div className="bg-emerald-500" style={{ width: `${sPos}%` }} />
            <div className="bg-muted-foreground/40" style={{ width: `${sNeu}%` }} />
            <div className="bg-rose-500" style={{ width: `${sNeg}%` }} />
          </div>
          <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
            <span>{sPos}% pos</span>
            <span>{sNeu}% neu</span>
            <span>{sNeg}% neg</span>
          </div>
        </div>
      ),
    },
  ];

  return (
    <section className="py-16 border-y border-border bg-muted/20">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {tiles.map((t, i) => (
            <motion.div
              key={t.label}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.08 }}
              className="bg-card border border-border rounded-xl p-5"
            >
              <div className="flex items-center justify-between mb-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <t.icon className="h-5 w-5 text-primary" />
                </div>
                {"change" in t && t.change ? (
                  <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded-full">
                    {t.change}
                  </span>
                ) : null}
              </div>
              {"custom" in t && t.custom ? (
                t.custom
              ) : (
                <>
                  <div className="font-display text-xl md:text-2xl font-bold truncate">
                    {isLoading ? "…" : t.value}
                  </div>
                </>
              )}
              <div className="text-xs text-muted-foreground mt-1">{t.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}